import { Button, SvgIcon } from '@mui/material';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { followCollection, unfollowCollection } from '../../actions/collections.actions';
import { palette, typography } from '../../assets/styles/themes/tokens';
import { NotificationContext } from '../../state/NotificationContextProvider';
import type { Collection } from '../../types/AppContent';
import getMuiIcon from '../../utils/getMuiIcon';
import { createFollowCuratedCollectionTrackingConfig } from '../../utils/tracking';

interface CuratedCollectionOverviewFollowButtonProps {
	collection: Collection;
}

const CuratedCollectionOverviewFollowButton = ({
	collection,
}: CuratedCollectionOverviewFollowButtonProps) => {
	const { t } = useTranslation();
	const { showNotification } = useContext(NotificationContext);
	const dispatch = useDispatch();

	const isFollowed = collection.isFollowing;

	const onClickButton = () => {
		if (isFollowed) {
			dispatch(unfollowCollection(collection));
			showNotification({
				message: t('You have successfully unsubscribed from the collection {{name}}.', {
					name: collection.title,
				}),
				severity: 'success',
			});
		} else {
			dispatch(followCollection(collection));
			showNotification({
				message: t('You have successfully subscribed to the collection {{name}}.', {
					name: collection.title,
				}),
				severity: 'success',
			});
		}

		createFollowCuratedCollectionTrackingConfig(isFollowed, collection);
	};

	return (
		<Button
			variant="outlined"
			size="medium"
			onClick={onClickButton}
			startIcon={
				<SvgIcon
					viewBox={isFollowed ? '0 0 24 24' : '0 0 19 19'}
					component={getMuiIcon(isFollowed ? 'check' : 'addCollection')}
					sx={{ fill: palette.primary.main, width: '18px', height: '18px' }}
				/>
			}
			sx={{
				...typography.button.small,
			}}
		>
			{t(isFollowed ? 'I follow' : 'Follow')}
		</Button>
	);
};

export default CuratedCollectionOverviewFollowButton;
